/**
 * I18nProvider — holds the active locale, persists it, and lazy-loads the
 * catalog for any non-default locale (#42, 2026-06-12).
 *
 * Design:
 *   - `en` is imported EAGERLY (it's the default + fallback) so the first
 *     paint never waits on a network chunk and there is never a blank flash.
 *   - `af` (and any future locale) is loaded via a dynamic `import()` the
 *     first time it's selected. Rollup emits each as its own async chunk, so
 *     the eager bundle only ever carries `en`. Loaded catalogs are cached in
 *     a module-level Map so re-selecting a locale is instant.
 *   - The chosen locale is persisted to localStorage and re-hydrated on mount;
 *     a persisted non-default locale kicks off its lazy load during hydration.
 *   - `t(key)` resolves against the active catalog, falling back to the `en`
 *     base for any key the active catalog is missing, then to the key string
 *     itself (so a wiring bug is visible, not blank).
 *
 * This is the ONE place that knows how to fetch a catalog; everything else
 * consumes `useTranslation()` / `useLocale()` from `hooks.ts`.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { EN } from './catalog-en';
import type { TranslationKey } from './catalog-en';
import { CATALOGS, loadCatalog } from './load';
import {
  DEFAULT_LOCALE,
  I18nContext,
  SUPPORTED_LOCALES,
  type I18nContextValue,
  type Locale,
} from './context';

const STORAGE_KEY = 'elreign-locale';

/** Read + validate the persisted locale; default `en` if absent/invalid. */
function readPersistedLocale(): Locale {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw && (SUPPORTED_LOCALES as readonly string[]).includes(raw)) {
      return raw as Locale;
    }
  } catch {
    /* localStorage unavailable (private mode / SSR) — use the default. */
  }
  return DEFAULT_LOCALE;
}

export function I18nProvider({ children }: { children: ReactNode }) {
  // `locale` is the SELECTED locale (may have a load in flight); `active` is
  // the locale whose catalog is actually rendering. They differ only briefly
  // while a lazy catalog loads — until then we keep showing the previous
  // (or en) strings, never a blank flash.
  const [locale, setLocaleState] = useState<Locale>(DEFAULT_LOCALE);
  const [active, setActive] = useState<Locale>(DEFAULT_LOCALE);
  const [loading, setLoading] = useState(false);
  // Force a re-render when a lazy catalog finishes loading (the Map mutates
  // in place, so we bump a counter to recompute the memoised context value).
  const [, bump] = useState(0);
  const mounted = useRef(true);

  // Hydrate the persisted locale once on mount. A persisted non-default
  // locale triggers its lazy load here.
  useEffect(() => {
    mounted.current = true;
    const persisted = readPersistedLocale();
    if (persisted !== DEFAULT_LOCALE) {
      setLocaleState(persisted);
      setLoading(true);
      void loadCatalog(persisted).then(() => {
        if (!mounted.current) return;
        setActive(persisted);
        setLoading(false);
        bump((n) => n + 1);
      });
    }
    return () => {
      mounted.current = false;
    };
  }, []);

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* persistence is best-effort; ignore quota/availability errors. */
    }
    const cached = CATALOGS.get(next);
    if (cached) {
      // Already loaded (en, or a previously-selected locale) — switch instantly.
      setActive(next);
      setLoading(false);
      return;
    }
    setLoading(true);
    void loadCatalog(next).then(() => {
      if (!mounted.current) return;
      setActive(next);
      setLoading(false);
      bump((n) => n + 1);
    });
  }, []);

  const catalog = CATALOGS.get(active) ?? EN;

  const t = useCallback(
    (key: TranslationKey): string => {
      // active catalog → en fallback → the key itself (never blank).
      return catalog[key] ?? EN[key] ?? key;
    },
    [catalog],
  );

  const value = useMemo<I18nContextValue>(
    () => ({ locale, t, setLocale, loading, catalog }),
    [locale, t, setLocale, loading, catalog],
  );

  return <I18nContext value={value}>{children}</I18nContext>;
}
